import React, { useState, useMemo } from 'react';
import {
  Shield, Key, Plus, Trash2, Eye, EyeOff, ChevronDown, ChevronRight,
  Globe, Lock, Copy, Check, AlertTriangle, Server,
} from 'lucide-react';
import { usePlatformStore } from '@/store/platformStore';
import type { Environment, EnvVariable, SecretRef } from '@/types/platform';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Secrets & Environments Panel                                      */
/*                                                                    */
/*  Native environment + secrets management. No .env files.           */
/*  No external Vault. Everything lives in the platform.              */
/* ================================================================== */

/* -- Environment badge --------------------------------------------- */

function EnvBadge({ slug }: { slug: string }) {
  const colors: Record<string, string> = {
    development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    staging: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    production: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <span className={cn(
      'px-1.5 py-0.5 rounded text-[10px] font-medium border',
      colors[slug] ?? 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    )}>
      {slug}
    </span>
  );
}

/* -- Variable row -------------------------------------------------- */

interface VariableRowProps {
  variable: EnvVariable;
  onDelete: (key: string) => void;
}

function VariableRow({ variable, onDelete }: VariableRowProps) {
  const [showValue, setShowValue] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(variable.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#2a2d2e] group text-[12px]">
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {variable.isSecret ? (
          <Lock className="w-3 h-3 text-yellow-500 shrink-0" />
        ) : (
          <Globe className="w-3 h-3 text-blue-400 shrink-0" />
        )}
        <span className="font-mono text-[#e0e0e0] truncate">{variable.key}</span>
      </div>

      <div className="flex items-center gap-1 min-w-0 flex-1">
        {variable.isSecret ? (
          <span className="font-mono text-[#666] text-[11px]">
            {showValue ? variable.value : '••••••••••••'}
          </span>
        ) : (
          <span className="font-mono text-[#9cdcfe] text-[11px] truncate">
            {variable.value}
          </span>
        )}
      </div>

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {variable.isSecret && (
          <button
            onClick={() => setShowValue(!showValue)}
            className="p-1 text-[#666] hover:text-white rounded"
            title={showValue ? 'Hide' : 'Reveal'}
          >
            {showValue ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
        )}
        <button
          onClick={handleCopy}
          className="p-1 text-[#666] hover:text-white rounded"
          title="Copy value"
        >
          {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
        </button>
        <button
          onClick={() => onDelete(variable.key)}
          className="p-1 text-[#666] hover:text-red-400 rounded"
          title="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* -- Secret row (server-managed, value never on client) ------------ */

function SecretRow({ secret, onDelete }: { secret: SecretRef; onDelete: (key: string) => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#2a2d2e] group text-[12px]">
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <Shield className="w-3 h-3 text-purple-400 shrink-0" />
        <span className="font-mono text-[#e0e0e0] truncate">{secret.key}</span>
      </div>

      <div className="flex items-center gap-1 min-w-0 flex-1">
        <span className="font-mono text-[#666] text-[11px]">{secret.maskedValue}</span>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-[9px] text-[#555]">
          {new Date(secret.updatedAt).toLocaleDateString()}
        </span>
        <button
          onClick={() => onDelete(secret.key)}
          className="p-1 text-[#666] hover:text-red-400 rounded"
          title="Delete secret"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* -- Add variable dialog ------------------------------------------- */

function AddVariableForm({ onAdd, onCancel }: {
  onAdd: (variable: EnvVariable) => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!key.trim()) return;
    onAdd({ key: key.trim().toUpperCase(), value, isSecret, description: description || undefined });
    setKey('');
    setValue('');
    setIsSecret(false);
    setDescription('');
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 border-t border-[#3e3e3e] space-y-2">
      <div className="flex gap-2">
        <input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="KEY_NAME"
          className="flex-1 bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-1 text-[12px] font-mono text-white placeholder-[#555] focus:border-[#007acc] outline-none"
          autoFocus
        />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="value"
          type={isSecret ? 'password' : 'text'}
          className="flex-1 bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-1 text-[12px] font-mono text-white placeholder-[#555] focus:border-[#007acc] outline-none"
        />
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] text-[#aaa] cursor-pointer">
          <input
            type="checkbox"
            checked={isSecret}
            onChange={(e) => setIsSecret(e.target.checked)}
            className="rounded border-[#3e3e3e] bg-[#1e1e1e]"
          />
          <Lock className="w-3 h-3" />
          Secret (encrypted)
        </label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="flex-1 bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-1 text-[11px] text-white placeholder-[#555] focus:border-[#007acc] outline-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 text-[11px] text-[#aaa] hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!key.trim()}
          className="px-3 py-1 text-[11px] bg-[#007acc] text-white rounded hover:bg-[#006bb3] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Add Variable
        </button>
      </div>
    </form>
  );
}

/* -- Environment section ------------------------------------------- */

interface EnvSectionProps {
  environment: Environment;
  isActive: boolean;
  onSelect: () => void;
}

function EnvironmentSection({ environment, isActive, onSelect }: EnvSectionProps) {
  const [isExpanded, setIsExpanded] = useState(isActive);
  const [showAddForm, setShowAddForm] = useState(false);

  const allVars = environment.variables;
  const secrets = environment.secrets;

  const handleDeleteVariable = (key: string) => {
    // In production: call platform.service.deleteEnvVariable
    console.log(`Delete variable ${key} from env ${environment.id}`);
  };

  const handleDeleteSecret = (key: string) => {
    // In production: call platform.service.deleteSecret
    console.log(`Delete secret ${key} from env ${environment.id}`);
  };

  const handleAddVariable = (variable: EnvVariable) => {
    // In production: call platform.service.setEnvVariable
    console.log(`Add variable to env ${environment.id}:`, variable);
    setShowAddForm(false);
  };

  return (
    <div className="border-b border-[#2d2d2d]">
      {/* Environment header */}
      <button
        onClick={() => { setIsExpanded(!isExpanded); onSelect(); }}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#2a2d2e] transition-colors',
          isActive && 'bg-[#04395e]/30',
        )}
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3 text-[#888] shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-[#888] shrink-0" />
        )}
        <Server className="w-3.5 h-3.5 text-[#aaa] shrink-0" />
        <span className="text-[12px] text-white font-medium flex-1">{environment.name}</span>
        <EnvBadge slug={environment.slug} />
        {isActive && (
          <div className="w-1.5 h-1.5 rounded-full bg-green-400" title="Active" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="bg-[#1e1e1e]/50">
          {/* Chain info */}
          <div className="px-3 py-1.5 text-[10px] text-[#666] flex items-center gap-3 border-b border-[#252526]">
            <span>Chain: {environment.chainId}</span>
            <span className="truncate">{environment.rpcUrl}</span>
          </div>

          {/* Variables */}
          {allVars.length > 0 && (
            <div>
              <div className="px-3 py-1 text-[10px] text-[#888] font-semibold uppercase tracking-wider">
                Variables ({allVars.length})
              </div>
              {allVars.map((v) => (
                <VariableRow key={v.key} variable={v} onDelete={handleDeleteVariable} />
              ))}
            </div>
          )}

          {/* Secrets */}
          {secrets.length > 0 && (
            <div>
              <div className="px-3 py-1 text-[10px] text-[#888] font-semibold uppercase tracking-wider">
                Secrets ({secrets.length})
              </div>
              {secrets.map((s) => (
                <SecretRow key={s.key} secret={s} onDelete={handleDeleteSecret} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {allVars.length === 0 && secrets.length === 0 && !showAddForm && (
            <div className="px-3 py-4 text-center text-[11px] text-[#666]">
              No variables or secrets configured
            </div>
          )}

          {/* Add form */}
          {showAddForm ? (
            <AddVariableForm
              onAdd={handleAddVariable}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] text-[#888] hover:text-white hover:bg-[#2a2d2e] transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add Variable
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Main Panel                                                        */
/* ================================================================== */

export default function SecretsEnvironmentPanel() {
  const activeProject = usePlatformStore((s) => s.activeProject);
  const activeEnvironment = usePlatformStore((s) => s.activeEnvironment);
  const setActiveEnvironment = usePlatformStore((s) => s.setActiveEnvironment);

  // Demo environments when no project loaded
  const demoEnvironments: Environment[] = useMemo(() => [
    {
      id: 'env-dev', projectId: 'demo', name: 'Development', slug: 'development',
      chainId: 11155111, rpcUrl: 'https://rpc.kubernodes.com/sepolia',
      isDefault: true, createdAt: new Date().toISOString(),
      secrets: [
        { key: 'PRIVATE_KEY', updatedAt: new Date().toISOString(), updatedBy: 'system', maskedValue: '0x...a3f2' },
        { key: 'ETHERSCAN_API_KEY', updatedAt: new Date().toISOString(), updatedBy: 'system', maskedValue: 'EK-...x9f1' },
      ],
      variables: [
        { key: 'SOLIDITY_VERSION', value: '0.8.24', isSecret: false, description: 'Compiler version' },
        { key: 'OPTIMIZER_RUNS', value: '200', isSecret: false },
        { key: 'GAS_REPORT', value: 'true', isSecret: false },
      ],
    },
    {
      id: 'env-staging', projectId: 'demo', name: 'Staging', slug: 'staging',
      chainId: 421614, rpcUrl: 'https://rpc.kubernodes.com/arb-sepolia',
      isDefault: false, createdAt: new Date().toISOString(),
      secrets: [
        { key: 'PRIVATE_KEY', updatedAt: new Date().toISOString(), updatedBy: 'system', maskedValue: '0x...b7c3' },
      ],
      variables: [
        { key: 'SOLIDITY_VERSION', value: '0.8.24', isSecret: false },
      ],
    },
    {
      id: 'env-prod', projectId: 'demo', name: 'Production', slug: 'production',
      chainId: 42161, rpcUrl: 'https://rpc.kubernodes.com/arbitrum',
      isDefault: false, createdAt: new Date().toISOString(),
      secrets: [
        { key: 'PRIVATE_KEY', updatedAt: new Date().toISOString(), updatedBy: 'system', maskedValue: '0x...e1a0' },
        { key: 'ETHERSCAN_API_KEY', updatedAt: new Date().toISOString(), updatedBy: 'system', maskedValue: 'EK-...m3k7' },
      ],
      variables: [
        { key: 'SOLIDITY_VERSION', value: '0.8.24', isSecret: false },
        { key: 'OPTIMIZER_RUNS', value: '1000', isSecret: false },
      ],
    },
  ], []);

  const environments = activeProject?.environments ?? demoEnvironments;
  const activeEnvId = activeEnvironment?.id ?? environments[0]?.id;

  const totalSecrets = environments.reduce((sum, e) => sum + e.secrets.length, 0);
  const totalVars = environments.reduce((sum, e) => sum + e.variables.length, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header stats */}
      <div className="flex items-center gap-3 px-3 py-2 text-[11px] text-[#888] border-b border-[#2d2d2d]">
        <div className="flex items-center gap-1">
          <Server className="w-3 h-3" />
          <span>{environments.length} envs</span>
        </div>
        <div className="flex items-center gap-1">
          <Key className="w-3 h-3 text-yellow-500" />
          <span>{totalSecrets} secrets</span>
        </div>
        <div className="flex items-center gap-1">
          <Globe className="w-3 h-3 text-blue-400" />
          <span>{totalVars} vars</span>
        </div>
      </div>

      {/* Security notice */}
      <div className="flex items-start gap-2 px-3 py-2 bg-yellow-500/5 border-b border-[#2d2d2d]">
        <AlertTriangle className="w-3 h-3 text-yellow-500 shrink-0 mt-0.5" />
        <p className="text-[10px] text-yellow-500/80">
          Secrets are encrypted at rest and injected at runtime. Values are never stored in code or sent to the client.
        </p>
      </div>

      {/* Environments list */}
      <div className="flex-1 overflow-y-auto">
        {environments.map((env) => (
          <EnvironmentSection
            key={env.id}
            environment={env}
            isActive={env.id === activeEnvId}
            onSelect={() => setActiveEnvironment(env)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-[#2d2d2d] text-[10px] text-[#555]">
        {activeProject ? activeProject.name : 'Demo Project'} · {environments.length} environments
      </div>
    </div>
  );
}
